import React, { useEffect, type ComponentPropsWithRef } from "react";
import { useField } from "formik";
import {
    RuleConditionAmount,
    TransferRuleDirection,
} from "@common/types/transactionRule";
import { Input } from "@design-system/Input/Input";
import { NumberFormat } from "@design-system/NumberFormat/NumberFormat";
import { Picker } from "@design-system/Picker/Picker.lib";
import {
    AmountConditionType,
    AmountRoundingConditionType,
    TransactionRuleConditionFormSchema,
} from "../../TransactionRuleForm/constants";
import styles from "../TransferRuleForm.module.scss";

const AmountInput = (props: Omit<ComponentPropsWithRef<"input">, "size">) => (
    <Input {...props} size="lg" className={styles.amountInput} />
);

interface Props {
    index: number;
    transferDirection: TransferRuleDirection;
}

const conditionTypeItems = Object.values(AmountConditionType).map((value) => ({
    value,
    label: value,
}));

const isCreditItems = [
    { value: "credit", label: "Cash in" },
    { value: "debit", label: "Cash out" },
];

const roundingLevelItems = Object.values(AmountRoundingConditionType).map(
    (value) => ({
        value,
        label: value,
    }),
);

export const AmountConditionForm: React.FC<Props> = ({
    index,
    transferDirection,
}) => {
    const [conditionTypeField, , conditionTypeHelpers] = useField<
        TransactionRuleConditionFormSchema["amountConditionType"]
    >(`conditions.${transferDirection}.${index}.amountConditionType`);
    const [, , isCreditHelpers] = useField<
        TransactionRuleConditionFormSchema["amountIsCredit"]
    >(`conditions.${transferDirection}.${index}.amountIsCredit`);
    const [singleValueField, , singleValueHelpers] = useField<
        TransactionRuleConditionFormSchema["amountSingleValue"]
    >(`conditions.${transferDirection}.${index}.amountSingleValue`);
    const [valueRangeField, , valueRangeHelpers] = useField<
        TransactionRuleConditionFormSchema["amountValueRange"]
    >(`conditions.${transferDirection}.${index}.amountValueRange`);
    const [roundingLevelField, , roundingLevelHelpers] = useField<
        TransactionRuleConditionFormSchema["amountRoundingLevel"]
    >(`conditions.${transferDirection}.${index}.amountRoundingLevel`);

    useEffect(() => {
        isCreditHelpers.setValue(
            transferDirection === TransferRuleDirection.TO,
        );
    }, [transferDirection, isCreditHelpers]);

    const isCreditDropdownValue =
        transferDirection === TransferRuleDirection.TO ? "credit" : "debit";

    const setRangeValue = (
        value: number | undefined,
        key: keyof RuleConditionAmount,
    ) => {
        valueRangeHelpers.setValue({
            ...(valueRangeField.value ?? {}),
            [key]: value,
        });
    };

    return (
        <>
            <Picker.Select
                items={isCreditItems}
                value={isCreditDropdownValue}
                onValueChange={(v) => {
                    if (v !== null) {
                        isCreditHelpers.setValue(v === "credit");
                    }
                }}
                placeholder="Select cash in or out"
                disabled
            />

            <Picker.Select
                items={conditionTypeItems}
                value={conditionTypeField.value ?? null}
                onValueChange={(v) => {
                    if (v !== null) {
                        conditionTypeHelpers.setValue(v);
                    }
                }}
                placeholder="Select type"
            />

            {[
                AmountConditionType.GREATER_THAN,
                AmountConditionType.LESS_THAN,
                AmountConditionType.IS,
            ].includes(conditionTypeField.value) && (
                <NumberFormat
                    value={singleValueField.value}
                    onValueChange={(v) =>
                        singleValueHelpers.setValue(v.floatValue)
                    }
                    prefix="$ "
                    decimalScale={2}
                    allowEmptyFormatting
                    thousandSeparator
                    allowNegative={false}
                    customInput={AmountInput}
                />
            )}
            {conditionTypeField.value === AmountConditionType.IS_ROUND && (
                <Picker.Select
                    items={roundingLevelItems}
                    value={roundingLevelField.value ?? null}
                    onValueChange={(v) => {
                        if (v !== null) {
                            roundingLevelHelpers.setValue(v);
                        }
                    }}
                    placeholder="Select rounding level"
                />
            )}
            {conditionTypeField.value === AmountConditionType.BETWEEN && (
                <>
                    <NumberFormat
                        value={valueRangeField.value?.min}
                        onValueChange={(v) =>
                            setRangeValue(v.floatValue, "min")
                        }
                        prefix="$ "
                        decimalScale={2}
                        allowEmptyFormatting
                        thousandSeparator
                        allowNegative={false}
                        customInput={AmountInput}
                    />
                    and
                    <NumberFormat
                        value={valueRangeField.value?.max}
                        onValueChange={(v) =>
                            setRangeValue(v.floatValue, "max")
                        }
                        prefix="$ "
                        decimalScale={2}
                        allowEmptyFormatting
                        thousandSeparator
                        allowNegative={false}
                        customInput={AmountInput}
                    />
                </>
            )}
        </>
    );
};
